import { Box, Container, Flex, Text, Heading } from '@radix-ui/themes';
import { useTenantContext } from '@trashlab/sitesapp/src/organizations/_default/contexts/use-tenant-context';
import { useState, useMemo, useEffect } from 'react';
import { Button } from '@trashlab/sitesapp/src/organizations/_default/template/components/shared/button';
import { Card } from '@trashlab/sitesapp/src/organizations/_default/template/components/shared/card';
import {
  IconBook,
  IconLoad,
  IconHaul,
} from '@trashlab/sitesapp/src/organizations/_default/template/components/icons/icons';
import { DynamicNumber } from '@trashlab/sitesapp/src/organizations/_default/template/components/icons/dynamic-number';
import { useVariantValue } from '@trashlab/sitesapp/src/organizations/_default/lib/utils/get-variant-value';
import { asAddress } from '@trashlab/api/src/library/server/utils/json-compatibility';

type VariantValue = {
  value: string;
  orderIndex?: number;
};

const StepDescription = ({
  description,
  isCarousel = false,
}: {
  description: string[];
  isCarousel?: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(isCarousel);

  return (
    <Flex direction="column" gap="2">
      <Box
        className={`relative ${
          !isCarousel && !isExpanded ? 'h-[80px] overflow-hidden' : ''
        }`}
      >
        {description.map((text, i) => (
          <Text key={i} size="2" className="text-gray-600">
            {text}
          </Text>
        ))}
        {!isCarousel && !isExpanded && (
          <Box className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent" />
        )}
      </Box>
      {!isCarousel && (
        <Button
          onClick={() => setIsExpanded(!isExpanded)}
          variant="soft"
          className="text-[var(--secondary-color)]"
          title={
            isExpanded
              ? 'Hide rental process details'
              : 'See complete rental process details'
          }
          aria-label={
            isExpanded
              ? 'Collapse the detailed rental step instructions'
              : 'Expand to read more about this rental step'
          }
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </Button>
      )}
    </Flex>
  );
};

export const RentalSteps = () => {
  const { website } = useTenantContext();
  const orgAddress = asAddress(website?.organization?.address as any);
  const city = orgAddress?.city ?? 'San Francisco';
  const state = orgAddress?.state ?? 'CA';
  const [isDesktop, setIsDesktop] = useState(true);

  const defaultSteps = [
    {
      title: 'Book your dumpster',
      subtitle: 'We deliver a dumpster to you',
      description: [
        `Begin your home cleanup or construction project with us. Simply select your desired dumpster size and schedule a convenient delivery date in ${city}.`,
        'Our dedicated team will deliver your dumpster right to your driveway, offering a hassle-free way to manage waste removal and junk disposal.',
      ],
      icon: IconBook,
      stepNumber: 1,
    },
    {
      title: 'Load the dumpster',
      subtitle: 'You load all your debris & waste',
      description: [
        `After receiving your dumpster rental in ${city}, you're ready to start loading it. From household junk to construction debris, toss in all your unwanted items.`,
        'Remember to load it evenly to maximize the use of space and ensure safety standards are met. Our affordable dumpster rental services make waste removal and junk disposal easy and convenient.',
      ],
      icon: IconLoad,
      stepNumber: 2,
    },
    {
      title: 'We haul it away',
      subtitle: 'We haul away your dumpster!',
      description: [
        `Once your dumpster rental in ${city} is filled, just give us a call. Our professional team will arrive promptly to haul away the dumpster and the unwanted items.`,
        "You won't need to worry about disposal or any cleanup; we handle everything, leaving you with a clean and clutter-free space.",
      ],
      icon: IconHaul,
      stepNumber: 3,
    },
  ];

  const iconMap = {
    IconBook,
    IconLoad,
    IconHaul,
  };

  const titles = useVariantValue<VariantValue[]>('HOW_TO_STEP_TITLE', []);
  const subtitles = useVariantValue<VariantValue[]>('HOW_TO_STEP_SUBTITLE', []);
  const contents = useVariantValue<VariantValue[]>('HOW_TO_STEP_CONTENT', []);
  const icons = useVariantValue<VariantValue[]>('HOW_TO_STEP_ICON', []);

  const rentalSubtitle = useVariantValue<string>(
    'HOW_TO_RENTAL_SUBTITLE',
    `The fastest way to rent a dumpster in ${city}, ${state}`,
  );
  const rentalTitle = useVariantValue<string>(
    'HOW_TO_RENTAL_TITLE',
    `How to rent a dumpster in ${city}`,
  );

  const steps = useMemo(() => {
    if (
      !titles.length &&
      !subtitles.length &&
      !contents.length &&
      !icons.length
    ) {
      return defaultSteps;
    }

    // Get all unique orderIndex values, handling undefined values properly
    const allOrderIndexes = [
      ...titles.map((t) => t.orderIndex),
      ...subtitles.map((s) => s.orderIndex),
      ...contents.map((c) => c.orderIndex),
      ...icons.map((i) => i.orderIndex),
    ].filter((index): index is number => typeof index === 'number');

    const orderIndexes =
      allOrderIndexes.length > 0
        ? [...new Set(allOrderIndexes)].sort((a, b) => a - b)
        : [0, 1, 2]; // Default to first 3 indexes if no orderIndex values

    return orderIndexes
      .map((index) => {
        const title = titles.find((t) => t.orderIndex === index)?.value;
        const subtitle = subtitles.find((s) => s.orderIndex === index)?.value;
        const content = contents.find((c) => c.orderIndex === index)?.value;
        const iconName = icons.find((i) => i.orderIndex === index)?.value;
        const icon = iconName
          ? iconMap[iconName as keyof typeof iconMap]
          : undefined;

        const defaultStep = defaultSteps[index];
        return {
          title: title ?? defaultStep?.title ?? '',
          subtitle: subtitle ?? defaultStep?.subtitle ?? '',
          description: content?.split('\n') ?? defaultStep?.description ?? [],
          icon: icon ?? defaultStep?.icon ?? IconBook,
          stepNumber: index + 1,
        };
      })
      .filter(
        (step) => step.title && step.subtitle && step.description.length > 0,
      );
  }, [titles, subtitles, contents, icons]);

  // Detect screen size
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 1024); // lg breakpoint is 1024px
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Container
      size="4"
      p="8"
      className={`relative min-h-[200px] overflow-hidden before:content-[''] before:absolute before:inset-0 before:bg-[url('/default/rental-steps-bg.svg')] before:bg-cover before:bg-center before:bg-no-repeat before:opacity-100 before:-z-10 after:content-[''] after:absolute after:inset-0 after:opacity-90 after:-z-10`}
    >
      <Flex direction="column" gap="2">
        <Text
          size="2"
          weight="bold"
          className="text-neutral-400"
          data-variable="HOW_TO_RENTAL_SUBTITLE"
        >
          {rentalSubtitle}
        </Text>
        <Heading as="h2" size="8" data-variable="HOW_TO_RENTAL_TITLE">
          {rentalTitle}
        </Heading>

        {isDesktop && (
          <Flex gap="4" className="mt-8">
            {steps.map((step, index) => (
              <div key={index} className="relative w-1/3">
                <Card direction="column" gap="2">
                  <Flex align="center" className="relative w-full">
                    <Box
                      data-variable="HOW_TO_STEP_ICON"
                      data-order-index={index}
                    >
                      <step.icon className="mr-auto" />
                    </Box>
                    <Box className="ml-auto w-[30%] flex justify-center items-center">
                      <Box className="w-[110px] flex justify-center">
                        <DynamicNumber
                          number={step.stepNumber}
                          className="opacity-30"
                        />
                      </Box>
                    </Box>
                  </Flex>
                  <Flex direction="column" gap="2">
                    <Text
                      size="2"
                      className="text-neutral-400 uppercase"
                      data-variable="HOW_TO_STEP_SUBTITLE"
                      data-order-index={index}
                    >
                      {step.subtitle}
                    </Text>
                    <Heading
                      size="4"
                      data-variable="HOW_TO_STEP_TITLE"
                      data-order-index={index}
                    >
                      {step.title}
                    </Heading>
                    <Box
                      data-variable="HOW_TO_STEP_CONTENT"
                      data-order-index={index}
                    >
                      <StepDescription description={step.description} />
                    </Box>
                  </Flex>
                </Card>
              </div>
            ))}
          </Flex>
        )}

        {/* Mobile horizontal scroll */}
        {!isDesktop && (
          <Box className="mt-8 -mx-8">
            <Box
              className="overflow-x-auto px-8 pb-4 hide-scrollbar"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <Flex gap="4" className="w-max">
                {steps.map((step, index) => (
                  <Box key={index} className="w-[280px]">
                    <Flex
                      direction="column"
                      gap="2"
                      className="relative p-6 rounded-md shadow-xl bg-white h-full before:absolute before:inset-0 before:shadow-[0_8px_24px_rgba(0,0,0,0.12)] after:absolute after:bottom-0 after:right-0 after:-z-10 after:h-full after:w-full after:translate-x-1 after:translate-y-1 [clip-path:polygon(40px_0,100%_0,100%_calc(100%_-_40px),calc(100%_-_40px)_100%,0_100%,0_40px)] before:[clip-path:polygon(40px_0,100%_0,100%_calc(100%_-_40px),calc(100%_-_40px)_100%,0_100%,0_40px)] after:[clip-path:polygon(40px_0,100%_0,100%_calc(100%_-_40px),calc(100%_-_40px)_100%,0_100%,0_40px)]"
                    >
                      <Flex align="center" className="relative w-full">
                        <Box
                          data-variable="HOW_TO_STEP_ICON"
                          data-order-index={index}
                        >
                          <step.icon className="mr-auto" />
                        </Box>
                        <Box className="ml-auto w-[30%] flex justify-center items-center">
                          <Box className="w-[110px] flex justify-center">
                            <DynamicNumber
                              number={step.stepNumber}
                              className="opacity-30"
                            />
                          </Box>
                        </Box>
                      </Flex>
                      <Flex direction="column" gap="2">
                        <Text
                          size="2"
                          className="text-neutral-400 uppercase"
                          data-variable="HOW_TO_STEP_SUBTITLE"
                          data-order-index={index}
                        >
                          {step.subtitle}
                        </Text>
                        <Heading
                          size="4"
                          data-variable="HOW_TO_STEP_TITLE"
                          data-order-index={index}
                        >
                          {step.title}
                        </Heading>
                        <Box
                          data-variable="HOW_TO_STEP_CONTENT"
                          data-order-index={index}
                        >
                          <StepDescription
                            description={step.description}
                            isCarousel
                          />
                        </Box>
                      </Flex>
                    </Flex>
                  </Box>
                ))}
              </Flex>
            </Box>
          </Box>
        )}
      </Flex>
    </Container>
  );
};

export default RentalSteps;
